import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Popover,
  Checkbox,
  ListItemText,
  IconButton,
} from "@mui/material";
import "./Inbound.css";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { fetchProcess } from "../../../services/processService";
import { fetchProcessSteps } from "../../../services/processStepService";
import { translateDbLabel } from "../../../utilities/translateDbLabel";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";

const formatBrDate = (date) => {
  if (!date) return "";
  return dayjs(date).format("DD/MM/YYYY");
};

const toIsoDate = (date) => {
  if (!date) return "";
  return dayjs(date).format("YYYY-MM-DD");
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return dayjs(a).isSame(dayjs(b), "day");
};

const isBetweenDates = (day, start, end) => {
  if (!start || !end) return false;
  const current = dayjs(day);
  const from = dayjs(start);
  const to = dayjs(end);
  return current.isAfter(from, "day") && current.isBefore(to, "day");
};

const calendarSx = {
  "& .MuiDayCalendar-weekContainer": {
    justifyContent: "space-around",
    gap: 0,       
    margin: 0,
  },
  "& .MuiDayCalendar-header": {
    justifyContent: "space-around",
  },
  "& .MuiPickersDay-root": {
    margin: 0,   
    borderRadius: 0,
  },
};

function RangeDay(props) {
  const { day, startDate, endDate, ...other } = props;

  const isStart = isSameDay(day, startDate);
  const isEnd = isSameDay(day, endDate);
  const isInRange = isBetweenDates(day, startDate, endDate);
  const isSingle = isStart && isSameDay(startDate, endDate);

  return (
    <PickersDay
      {...other}
      day={day}
      selected={false}
      sx={{
        width: 36,      // 👈 largura total da célula
        height: 34,
        fontSize: "0.8rem",
        margin: 0,
        borderRadius: isSingle
          ? "6px !important"
          : isStart
          ? "6px 0 0 6px !important"
          : isEnd
          ? "0 6px 6px 0 !important"
          : isInRange
          ? "0 !important"
          : "6px !important",
        backgroundColor: (theme) =>
          isStart || isEnd
            ? `${theme.palette.primary.main} !important`
            : isInRange
            ? `${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.3 : 0.12)} !important`
            : undefined,
        color: (theme) =>
          isStart || isEnd
            ? `${theme.palette.primary.contrastText} !important`
            : isInRange
            ? `${theme.palette.text.primary} !important`
            : undefined,
        "&:hover": {
          backgroundColor: (theme) =>
            isStart || isEnd
              ? `${theme.palette.primary.dark} !important`
              : isInRange
              ? `${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.44 : 0.24)} !important`
              : undefined,
        },
      }}
    />
  );
}

const InboundFilter = ({
  filters,
  onFiltersChange,
  onSearch,
  loading,
  error,
}) => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language?.startsWith("en");

  const docTypeOptions = [
    { value: "nfe", labelKey: "docType.nfe" },
    { value: "cte", labelKey: "docType.cte" },
  ];

  const [processOptions, setProcessOptions] = useState([]);
  const [stepOptions, setStepOptions] = useState([]);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const [rangeStart, setRangeStart] = useState(
    filters.issueDateFrom ? dayjs(filters.issueDateFrom) : null,
  );
  const [rangeEnd, setRangeEnd] = useState(
    filters.issueDateTo ? dayjs(filters.issueDateTo) : null,
  );
  const [selectingEnd, setSelectingEnd] = useState(false);

  const [receivedCalendarAnchorEl, setReceivedCalendarAnchorEl] = useState(null);
  const [receivedRangeStart, setReceivedRangeStart] = useState(
    filters.receivedDateFrom ? dayjs(filters.receivedDateFrom) : null,
  );
  const [receivedRangeEnd, setReceivedRangeEnd] = useState(
    filters.receivedDateTo ? dayjs(filters.receivedDateTo) : null,
  );
  const [receivedSelectingEnd, setReceivedSelectingEnd] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  const today = dayjs();

  const updateFilters = (changes) => {
    onFiltersChange({ ...filters, ...changes });
  };

  const openCalendar = (event) => setCalendarAnchorEl(event.currentTarget);
  const closeCalendar = () => setCalendarAnchorEl(null);

  const handleCalendarChange = (newDate) => {
    if (!newDate) return;

    if (!selectingEnd || !rangeStart) {
      setRangeStart(newDate);
      setRangeEnd(newDate);
      setSelectingEnd(true);
      updateFilters({
        issueDateRange: formatBrDate(newDate),
        issueDateFrom: toIsoDate(newDate),
        issueDateTo: toIsoDate(newDate),
      });
      return;
    }

    let start = rangeStart;
    let end = newDate;
    if (dayjs(end).isBefore(dayjs(start), "day")) [start, end] = [end, start];

    const sameDay = dayjs(start).isSame(dayjs(end), "day");
    setRangeStart(start);
    setRangeEnd(end);
    setSelectingEnd(false);
    updateFilters({
      issueDateRange: sameDay
        ? formatBrDate(start)
        : `${formatBrDate(start)} - ${formatBrDate(end)}`,
      issueDateFrom: toIsoDate(start),
      issueDateTo: toIsoDate(end),
    });
    closeCalendar();
  };

  const clearDateRange = () => {
    setRangeStart(null);
    setRangeEnd(null);
    setSelectingEnd(false);
    updateFilters({ issueDateRange: "", issueDateFrom: "", issueDateTo: "" });
  };

  const openReceivedCalendar = (event) => setReceivedCalendarAnchorEl(event.currentTarget);
  const closeReceivedCalendar = () => setReceivedCalendarAnchorEl(null);

  const handleReceivedCalendarChange = (newDate) => {
    if (!newDate) return;

    if (!receivedSelectingEnd || !receivedRangeStart) {
      setReceivedRangeStart(newDate);
      setReceivedRangeEnd(newDate);
      setReceivedSelectingEnd(true);
      updateFilters({
        receivedDateRange: formatBrDate(newDate),
        receivedDateFrom: toIsoDate(newDate),
        receivedDateTo: toIsoDate(newDate),
      });
      return;
    }

    let start = receivedRangeStart;
    let end = newDate;
    if (dayjs(end).isBefore(dayjs(start), "day")) [start, end] = [end, start];

    const sameDay = dayjs(start).isSame(dayjs(end), "day");
    setReceivedRangeStart(start);
    setReceivedRangeEnd(end);
    setReceivedSelectingEnd(false);
    updateFilters({
      receivedDateRange: sameDay
        ? formatBrDate(start)
        : `${formatBrDate(start)} - ${formatBrDate(end)}`,
      receivedDateFrom: toIsoDate(start),
      receivedDateTo: toIsoDate(end),
    });
    closeReceivedCalendar();
  };

  const clearReceivedDateRange = () => {
    setReceivedRangeStart(null);
    setReceivedRangeEnd(null);
    setReceivedSelectingEnd(false);
    updateFilters({ receivedDateRange: "", receivedDateFrom: "", receivedDateTo: "" });
  };

  useEffect(() => {
    if (filters.issueDateFrom && filters.issueDateTo) {
      const start = dayjs(filters.issueDateFrom);
      const end = dayjs(filters.issueDateTo);
      const sameDay = start.isSame(end, "day");
      const rangeText = sameDay
        ? start.format("DD/MM/YYYY")
        : `${start.format("DD/MM/YYYY")} - ${end.format("DD/MM/YYYY")}`;
      onFiltersChange({ ...filters, issueDateRange: rangeText });
    }
  }, []);

  useEffect(() => {
    const loadProcesses = async () => {
      try {
        const data = await fetchProcess();
        setProcessOptions(
          data.map((item) => ({
            value: String(item.process),
            label: translateDbLabel(item.description_process, isEnglish),
          })),
        );
      } catch (error) {
        console.error("Erro ao buscar processos:", error);
      }
    };
    loadProcesses();
  }, [isEnglish]);

  useEffect(() => {
    const loadSteps = async () => {
      try {
        if (!filters.process || filters.process.length === 0) {
          setStepOptions([]);
          if (filters.step?.length) updateFilters({ step: [] });
          return;
        }
        const data = await fetchProcessSteps(filters.process);
        const formattedSteps = data.map((item) => ({
          value: String(item.step),
          label: translateDbLabel(item.description_step, isEnglish),
          process: String(item.process),
        }));
        setStepOptions(formattedSteps);
        if (filters.step?.length) updateFilters({ step: [] });
      } catch (error) {
        console.error("Erro ao buscar etapas:", error);
        setStepOptions([]);
      }
    };
    loadSteps();
  }, [filters.process, isEnglish]);

  const handleDocTypeChange = (e) => {
    updateFilters({ docType: e.target.value });
  };

  return (
    <Box className="nfeGrid" sx={{ p: 0, m: 0, boxSizing: "border-box" }}>
      <Box sx={{ pt: 0 }}>
        <Paper
          variant="outlined"
          className="filterCard"
          sx={{ paddingBottom: showFilters ? "24px" : "8px" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FilterListIcon sx={{ fontSize: 18, color: "primary.main" }} />
              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                {t("filters.title")}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setShowFilters(!showFilters)}
              sx={{ color: "text.secondary", opacity: 0.6, "&:hover": { background: "transparent", opacity: 1 } }}
            >
              <ExpandMoreIcon sx={{ transform: showFilters ? "rotate(180deg)" : "none", transition: "0.2s" }} />
            </IconButton>
          </Box>

          {showFilters && (
            <div className="filterRow">
              <div className="filterLine">
                <TextField
                  select
                  label={t("filters.docType")}
                  value={filters.docType}
                  onChange={handleDocTypeChange}
                  className="compactField field-228"
                >
                  {docTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</MenuItem>
                  ))}
                </TextField>

                {filters.docType !== "cte" && (
                  <>
                    <TextField
                      select
                      label={t("filters.process")}
                      value={filters.process}
                      onChange={(e) => updateFilters({ process: e.target.value, step: [] })}
                      className="compactField field-228"
                      SelectProps={{
                        multiple: true,
                        renderValue: (selected) =>
                          selected.map((val) => processOptions.find((opt) => opt.value === val)?.label).filter(Boolean).join(", "),
                      }}
                    >
                      {processOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value} sx={{ py: 0.5, minHeight: 36, fontSize: "14px" }}>
                          <Checkbox checked={filters.process.indexOf(opt.value) > -1} />
                          <ListItemText primary={opt.label} slotProps={{ primary: { sx: { fontSize: "14px", fontWeight: 400 } } }} />
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      select
                      label={t("filters.step")}
                      value={filters.step}
                      onChange={(e) => updateFilters({ step: e.target.value })}
                      className="compactField field-228"
                      SelectProps={{
                        multiple: true,
                        renderValue: (selected) =>
                          selected.map((val) => stepOptions.find((opt) => opt.value === val)?.label).filter(Boolean).join(", "),
                      }}
                    >
                      {stepOptions.map((opt) => (
                        <MenuItem key={`${opt.process}-${opt.value}`} value={opt.value} sx={{ py: 0.5, minHeight: 36, fontSize: 14 }}>
                          <Checkbox checked={filters.step.indexOf(opt.value) > -1} />
                          <ListItemText primary={opt.label} primaryTypographyProps={{ sx: { fontSize: 14 } }} />
                        </MenuItem>
                      ))}
                    </TextField>
                  </>
                )}

                <TextField
                  label={t("filters.issueDate")}
                  value={filters.issueDateRange || ""}
                  className="compactField field-382"
                  onClick={openCalendar}
                  inputProps={{ readOnly: true }}
                />

                <Popover
                  open={Boolean(calendarAnchorEl)}
                  anchorEl={calendarAnchorEl}
                  onClose={closeCalendar}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  PaperProps={{ className: "issueDatePopover" }}
                >
                  <Box sx={{ p: 1, pb: 0 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateCalendar
                        defaultValue={today}
                        value={rangeEnd || rangeStart || today}
                        onChange={handleCalendarChange}
                        slots={{ day: RangeDay }}
                        slotProps={{ day: { startDate: rangeStart, endDate: rangeEnd } }}
                        sx={calendarSx}
                      />
                    </LocalizationProvider>
                    <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                      <Button size="small" onClick={clearDateRange} sx={{ textTransform: "none" }}>
                        {t("buttons.bClear")}
                      </Button>
                    </Box>
                  </Box>
                </Popover>

                <TextField
                  label={t("filters.receivedDate")}
                  value={filters.receivedDateRange || ""}
                  size="small"
                  className="compactField field-382"
                  InputLabelProps={{ shrink: true }}
                  onClick={openReceivedCalendar}
                  inputProps={{ readOnly: true }}
                />

                <Popover
                  open={Boolean(receivedCalendarAnchorEl)}
                  anchorEl={receivedCalendarAnchorEl}
                  onClose={closeReceivedCalendar}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  PaperProps={{ className: "issueDatePopover" }}
                >
                  <Box sx={{ p: 1, pb: 0 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateCalendar
                        defaultValue={today}
                        value={receivedRangeEnd || receivedRangeStart || today}
                        onChange={handleReceivedCalendarChange}
                        slots={{ day: RangeDay }}
                        slotProps={{ day: { startDate: receivedRangeStart, endDate: receivedRangeEnd } }}
                        sx={calendarSx}
                      />
                    </LocalizationProvider>
                    <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                      <Button size="small" onClick={clearReceivedDateRange} sx={{ textTransform: "none" }}>
                        {t("buttons.bClear")}
                      </Button>
                    </Box>
                  </Box>
                </Popover>
              </div>

              <div className="filterLine">
                <TextField
                  label={t("filters.supplier_cnpj")}
                  value={filters.supplier_cnpj}
                  onChange={(e) => updateFilters({ supplier_cnpj: e.target.value })}
                  className="compactField field-130"
                />

                {filters.docType !== "cte" && (
                  <TextField
                    label={t("filters.plant")}
                    value={filters.plant}
                    onChange={(e) => updateFilters({ plant: e.target.value })}
                    className="compactField field-70"
                  />
                )}

                <TextField
                  label={t("filters.accessKey")}
                  value={filters.accessKey}
                  onChange={(e) => updateFilters({ accessKey: e.target.value })}
                  className="compactField field-335"
                />

                <Button
                  type="button"
                  variant="contained"
                  className="btnBuscar"
                  onClick={onSearch}
                  disabled={loading}
                >
                  {loading ? "Buscando..." : t("filters.search")}
                </Button>
              </div>
            </div>
          )}

          {error && (
            <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default InboundFilter;